/**
 * Централизованная обработка истечения сессии и авторизации
 */

import type { MessageInstance } from 'antd/es/message/interface';
import type { TFunction } from 'i18next';
import { useAuthStore } from '@features/auth/store/authStore';
import { isTokenExpired } from './jwt';
import { getWebSocketNotificationApi } from './websocketNotificationApi';
import { saveRedirectPath } from './redirectAfterLogin';
import { isSessionlessAuthRequest } from '@shared/api/sessionlessAuth';

/**
 * Применима ли вообще политика «сессия истекла» к упавшему запросу.
 *
 * ЕДИНСТВЕННОЕ место, где принимается это решение. Оба axios-клиента,
 * Connect-клиент, AuthProvider, ProtectedRoute и стрим мониторинга ходят сюда
 * же — им запрещено заводить свою копию условия: ровно из-за копии предиката
 * правка однажды легла в клиент, через который вход не ходит.
 *
 * false = запрос сам был попыткой аутентификации. Сессии там ещё нет:
 * сбрасывать нечего, продлевать нечего, а редирект перезагружает страницу
 * входа и уничтожает сообщение об ошибке до того, как оператор его увидит.
 */
export function isSessionExpiryApplicable(requestUrl?: string): boolean {
    return !isSessionlessAuthRequest(requestUrl);
}

/**
 * Обрабатывает истечение сессии
 * Очищает токены, показывает уведомление и перенаправляет на страницу входа
 *
 * @param reason - Причина истечения сессии (для уведомления)
 * @param options - Опции обработки
 * @param options.messageApi - Message API для уведомлений (опционально, используется глобальный если не передан)
 * @param options.t - Функция перевода (опционально, используется статический текст если не передан)
 * @param options.showNotification - Показывать ли уведомление (по умолчанию true)
 * @param options.redirectToLogin - Перенаправлять ли на страницу входа (по умолчанию true)
 * @param options.requestUrl - URL упавшего запроса. Именно по нему воронка решает,
 *   применима ли политика: window.location не годится — форму входа могут
 *   встроить в модалку или другой роут.
 */
export function handleSessionExpired(
    reason: 'expired' | 'refresh_failed' | 'unauthorized' = 'expired',
    options: {
        messageApi?: MessageInstance;
        t?: any;
        showNotification?: boolean;
        redirectToLogin?: boolean;
        requestUrl?: string;
    } = {},
): void {
    const {
        messageApi,
        t,
        showNotification = true,
        redirectToLogin = true,
        requestUrl,
    } = options;

    // Упал сам запрос входа — политика неприменима целиком: ни сброса, ни
    // уведомления, ни редиректа. Ошибку покажет вызывающий экран.
    if (!isSessionExpiryApplicable(requestUrl)) {
        return;
    }

    // Очистить токены и состояние
    const { logout } = useAuthStore.getState();
    logout();

    // Показать уведомление
    if (showNotification) {
        const message = messageApi || getWebSocketNotificationApi().message;

        if (message) {
            const messages = {
                expired: {
                    text: t?.('auth:sessionExpired', 'Сессия истекла') || 'Сессия истекла',
                    description: t?.('auth:sessionExpiredDescription', 'Ваша сессия истекла. Пожалуйста, войдите снова.') || 'Ваша сессия истекла. Пожалуйста, войдите снова.',
                },
                refresh_failed: {
                    text: t?.('auth:tokenRefreshFailed', 'Не удалось обновить токен') || 'Не удалось обновить токен',
                    description: t?.('auth:tokenRefreshFailedDescription', 'Пожалуйста, войдите снова.') || 'Пожалуйста, войдите снова.',
                },
                unauthorized: {
                    text: t?.('auth:unauthorized', 'Требуется авторизация') || 'Требуется авторизация',
                    description: t?.('auth:unauthorizedDescription', 'Для доступа к этой странице необходимо войти в систему.') || 'Для доступа к этой странице необходимо войти в систему.',
                },
            };

            const msg = messages[reason];
            message.error(msg.text, 5);
        } else {
            // Fallback если message API недоступен
            console.warn('[handleSessionExpired] Message API недоступен');
        }
    }

    // Перенаправить на страницу входа
    if (redirectToLogin) {
        // Сохранить текущий путь для редиректа после входа
        saveRedirectPath();

        // Используем window.location для гарантированного редиректа
        // (работает даже если React Router не инициализирован)
        window.location.href = '/login';
    }
}

/**
 * Проверяет, истек ли текущий токен
 * @returns true если токен истек
 */
export function checkTokenExpiration(): boolean {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    return isTokenExpired(token);
}

/**
 * Очищает сессию без уведомлений и редиректа
 * Используется для программного выхода
 */
export function clearSession(): void {
    const { logout } = useAuthStore.getState();
    logout();
}

